/* ============================ ACCOUNT PAGES: org / audit / settings ============================ */
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { DB, act, api, can, createStore, fire, job, load, toast } from "../lib/store";
import { ago, inFuture, when } from "../lib/fmt";
import { Chips, copy } from "../ui/common";
import { Btn, Modal, useForm, type Form } from "../ui/modal";
import { PAGES } from "./registry";
import { HEALTH_LABELS, globalHealth } from "./fleet";
import { deleteOrg, invite, newKey, newOrg, renameOrg, switchOrg, userScope } from "../modals/account";

/* ---------- organization ---------- */
function RoleSel({ u }: { u: any }) {
  return (
    <select value={u.role} disabled={!(can("Admin") && u.id !== DB.me.id)}
      onChange={(e) => { const v = e.target.value; fire(act("PUT", `/api/users/${u.id}`, { role: v }, "Role updated", u.name + " is now " + v)); }}>
      {["Owner", "Admin", "Operator", "Viewer"].map((r) => <option key={r}>{r}</option>)}
    </select>
  );
}

function OrgPage() {
  const pa = DB.me.platform_admin;
  return (
    <>
      <div className="pagehead">
        <div><h2>{DB.org.org || "Organization"}</h2>
          <Chips items={[[DB.users.length, "people"], [DB.networks.filter((n: any) => n.org_id === DB.org.org_id).length, "networks"], pa ? [DB.orgs.length, "organizations in total", "acc"] : null]} />
          <p>People, what they can reach, and the keys that let other systems in. Everyone here sees only this organization{pa ? "; platform admins see all of them" : ""}.</p></div>
        <div className="spacer" />
        <div className="btnrow">
          {pa ? <button className="btn" onClick={() => newOrg()}>New organization</button> : null}
          {can("Admin") ? <button className="btn primary" onClick={() => invite()}>Invite someone</button> : null}
        </div>
      </div>
      {pa ? <>
        <div className="section-title" style={{ marginTop: 0 }}>All organizations <span className="badge acc">platform admin</span></div>
        <div className="tbl-wrap" style={{ marginBottom: 18 }}><table><thead><tr><th>Organization</th><th>Networks</th><th>Devices</th><th>People</th><th></th></tr></thead><tbody>
          {(DB.orgs || []).map((o: any) => (
            <tr key={o.id} className="row"><td><b style={{ fontWeight: 500 }}>{o.name}</b>{o.id === DB.org.org_id ? <> <span className="badge ok">open now</span></> : null}</td>
              <td>{(o.networks || []).map((n: any) => n.name).join(", ") || "—"}</td><td>{o.devices}</td><td>{o.users}</td>
              <td style={{ textAlign: "right" }}><div className="btnrow" style={{ justifyContent: "flex-end" }}>
                {o.id !== DB.org.org_id ? <button className="btn sm" onClick={() => switchOrg(o.id)}>Open</button> : null}
                <button className="btn sm ghost" onClick={() => renameOrg(o.id)}>Rename</button>
                {o.id !== DB.me.org_id ? <button className="btn sm ghost" onClick={() => deleteOrg(o.id)}>Delete</button> : null}
              </div></td>
            </tr>
          ))}
        </tbody></table></div>
        <div className="section-title">People in {DB.org.org || ""}</div>
      </> : null}
      <div className="tbl-wrap"><table><thead><tr><th>Person</th><th>Role</th><th>Can reach</th><th>Two-factor</th><th>Last active</th><th></th></tr></thead><tbody>
        {DB.users.map((u: any) => (
          <tr key={u.id} className="row">
            <td><b style={{ fontWeight: 500 }}>{u.name}</b>{u.pending ? <> <span className="badge acc">invited</span></> : null}
              {u.platform_admin ? <> <span className="badge warn">platform admin</span></> : null}
              <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{u.email}</div></td>
            <td><RoleSel u={u} /></td>
            <td>{can("Admin") ? <a style={{ cursor: "pointer" }} onClick={() => userScope(u.id)}>{u.scope}</a> : u.scope}</td>
            <td>{u.mfa ? <span className="badge ok"><span className="dot" />on</span>
              : <span className="badge warn"><span className="dot" />{u.mfa_required ? "required, not set up" : "off"}</span>}</td>
            <td>{u.last ? ago(u.last) : "never"}</td>
            <td style={{ textAlign: "right" }}>{can("Admin") && u.id !== DB.me.id ? (
              <button className="btn sm ghost" onClick={() => Modal.confirm(`Remove ${u.email}?`, "They lose access immediately. Their API keys stop working.", "Remove",
                () => fire(act("DELETE", `/api/users/${u.id}`, undefined, "Access removed", u.name)))}>Remove</button>) : null}</td>
          </tr>
        ))}
      </tbody></table></div>
      <div className="section-title">What each role can do</div>
      <div className="grid g4">{[
        ["Owner", "Everything, including managing other Owners."],
        ["Admin", "Create and delete workloads, manage people, sites, devices and settings."],
        ["Operator", "Deploy, restart, snapshot, back up and roll back. Cannot delete anything."],
        ["Viewer", "Read dashboards, logs and job history."],
      ].map(([r, d]) => (
        <div key={r} className="card"><div className="bd"><b>{r}</b><p style={{ color: "var(--ink-2)", fontSize: 13, margin: "6px 0 0" }}>{d}</p></div></div>
      ))}</div>
      {can("Admin") ? <>
        <div className="section-title">API keys</div>
        <KeysCard />
        <p className="hint">Send a key as <code>Authorization: Bearer ec_live_…</code>. Interactive docs: <a href="/api/docs" target="_blank">/api/docs</a>.</p>
      </> : null}
    </>
  );
}
PAGES.org = { crumb: () => "Organization", C: OrgPage };

/* ---------- API keys ---------- */
const keysVer = createStore(0);
/* ask every mounted KeysCard to re-read /api/keys (old Keys.load()) */
export const reloadKeys = () => keysVer.set((v) => v + 1);
export async function rotateKey(id: any) {
  try { const r = await api("POST", `/api/keys/${id}/rotate`); Modal.showKey(r.key, "The old key keeps working for 24 hours.", reloadKeys); }
  catch (e: any) { toast("Failed", e.message, "err"); }
}
export function KeysCard() {
  const ver = keysVer.use();
  const [ks, setKs] = useState<any[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api("GET", "/api/keys").then(
      (r: any) => { if (alive) { setKs(r); setErr(null); } },
      (e: any) => { if (alive) setErr(e.message); },
    );
    return () => { alive = false; };
  }, [ver]);
  let body: ReactNode;
  if (err != null) body = <p style={{ padding: 14 }}>{err}</p>;
  else if (!ks) body = <div className="empty" style={{ padding: 20 }}><span className="spin" /></div>;
  else if (!ks.length) body = <p style={{ padding: 14 }} className="muted">No API keys yet.</p>;
  else body = (
    <table><thead><tr><th>Name</th><th>Key</th><th>Scopes</th><th>Created</th><th>Last used</th><th></th></tr></thead><tbody>
      {ks.map((k: any) => (
        <tr key={k.id}>
          <td>{k.name}<div className="muted" style={{ fontSize: 12 }}>{k.owner}</div></td>
          <td className="mono">{k.prefix}••••••••{k.expires ? <> <span className="badge warn">expires {inFuture(k.expires)}</span></> : null}</td>
          <td>{k.scopes}</td><td>{when(k.created)}</td><td>{k.last_used ? ago(k.last_used) : "never"}</td>
          <td style={{ textAlign: "right" }}><div className="btnrow" style={{ justifyContent: "flex-end" }}>
            <button className="btn sm" onClick={() => rotateKey(k.id)}>Rotate</button>
            <button className="btn sm ghost" onClick={() => Modal.confirm(`Revoke ${k.name}?`, "Anything using it stops working now.", "Revoke",
              () => fire((async () => { await act("DELETE", `/api/keys/${k.id}`, undefined, "Key revoked"); reloadKeys(); })()))}>Revoke</button>
          </div></td>
        </tr>
      ))}
    </tbody></table>
  );
  return (
    <div className="card">
      <div className="hd"><h3>Keys</h3><div className="spacer" /><button className="btn sm" onClick={() => newKey()}>Create key</button></div>
      <div className="bd" id="keys" style={{ padding: 0 }}>{body}</div>
    </div>
  );
}

/* ---------- audit ---------- */
function AuditPage() {
  return (
    <>
      <div className="pagehead">
        <div><h2>Audit log</h2><p>Every change, who made it and from where. Export it to your SIEM.</p></div>
        <div className="spacer" />
        <a className="btn" href="/api/audit.csv" onClick={() => toast("Export started", "CSV will download shortly")}>Export CSV</a>
      </div>
      <div className="tbl-wrap"><table><thead><tr><th>When</th><th>Who</th><th>What</th><th>Where</th><th>Source IP</th></tr></thead><tbody>
        {DB.audit.length ? DB.audit.map((a: any, i: number) => (
          <tr key={a.id ?? i} className="row"><td>{when(a.t)}</td><td>{a.who}</td><td>{a.what}</td><td>{a.where}</td><td className="mono">{a.ip}</td></tr>
        )) : <tr><td colSpan={5} className="muted">Nothing yet.</td></tr>}
      </tbody></table></div>
    </>
  );
}
PAGES.audit = { crumb: () => "Audit log", C: AuditPage };

/* ---------- settings ---------- */
export const POLICY_PROFILES: Record<string, Record<string, string>> = {
  strict: { privileged: "error", mem_limit: "error", latest: "warn", secrets: "warn", host_network: "warn" },
  balanced: { privileged: "error", mem_limit: "warn", latest: "warn", secrets: "warn", host_network: "off" },
  relaxed: { privileged: "warn", mem_limit: "off", latest: "off", secrets: "off", host_network: "off" },
};
const LV: [string, string][] = [["error", "Block deploy (error)"], ["warn", "Warn only"], ["off", "Ignore"]];
const RULES: [string, ReactNode][] = [
  ["privileged", <><code>privileged: true</code> containers</>],
  ["mem_limit", "Services without a memory limit"],
  ["latest", <><code>:latest</code> or untagged images</>],
  ["host_network", <code>network_mode: host</code>],
  ["secrets", "Passwords written inline instead of ${VAR}"],
];
const PROFILES: [string, string][] = [
  ["strict", "Strict — recommended for production (current default)"],
  ["balanced", "Balanced — memory limits and host networking become warnings"],
  ["relaxed", "Relaxed — only syntax and a dry run must pass"],
  ["custom", "Custom — pick each rule below"],
];
const WINDOWS = ["Sunday 02:00 – 04:00", "Saturday 02:00 – 04:00", "Daily 01:00 – 03:00", "Daily 02:00 – 05:00", "No restriction"];

const Settings = {
  /* switching profile fills in the rule levels and locks them unless "custom" */
  profile(f: Form, v: string) {
    const pr = POLICY_PROFILES[v];
    f.d["p-profile"] = v;
    if (pr) RULES.forEach(([k]) => { f.d["p-" + k] = pr[k]; });
    f.refresh();
  },
  savePolicy(f: Form) {
    const body: any = { profile: f.d["p-profile"], registry_allowlist: !!f.d["p-reg"], registries: f.d["p-regs"] ?? "", allow_override: !!f.d["p-ovr"] };
    RULES.forEach(([k]) => { body[k] = f.d["p-" + k]; });
    return Settings.save("policy", body);
  },
  async save(section: string, body: any, quiet?: boolean) {
    try { await api("PUT", "/api/settings/" + section, body); if (!quiet) toast("Settings saved", section); await load(); }
    catch (e: any) { toast("Not saved", e.message, "err"); }
  },
  async test() {
    try {
      const r = await api("POST", "/api/notify/test", {});
      toast(r.sent.length ? "Test sent" : "Nothing sent", r.sent.join(", ") || "Configure at least one channel and save first",
        r.sent.some((x: string) => x.startsWith("error")) ? "warn" : "");
    } catch (e: any) { toast("Failed", e.message, "err"); }
  },
};

function SettingsPage() {
  const S = DB.settings, p = S.policy || {}, c = S.connectivity || {}, w = S.window || {}, b = S.branding || {}, nf = S.notifications || {}, g = S.guest || {};
  const ad = can("Admin"), pa = !!DB.me.platform_admin;
  const dis = !ad, pdis = !(ad && pa);
  const pick = (v: any, allowed: string[]) => (allowed.includes(v) ? v : allowed[0]);
  const f = useForm((() => {
    const init: any = {
      "p-profile": pick(p.profile, PROFILES.map(([v]) => v)),
      "p-reg": !!p.registry_allowlist, "p-regs": p.registries || "", "p-ovr": p.allow_override !== false,
      "c-in": c.checkin_s ?? "", "c-off": c.offline_after_s ?? "", "c-ret": c.retention_days ?? "", "c-ov": c.overlay ?? "",
      "c-ts": "", "c-tags": c.tailscale_tags ?? "",
      "w-val": pick(w.value, WINDOWS), "w-tz": w.tz ?? "",
      "b-name": b.name ?? "", "b-dom": b.domain ?? "", "g-user": g.vm_user ?? "",
      "n-host": nf.smtp_host ?? "", "n-port": nf.smtp_port ?? "", "n-user": nf.smtp_user ?? "", "n-pass": "",
      "n-from": nf.smtp_from ?? "", "n-to": nf.email_to ?? "", "n-slack": nf.slack_webhook ?? "", "n-pd": nf.pagerduty_key ?? "", "n-hook": nf.webhook_url ?? "",
    };
    RULES.forEach(([k]) => { init["p-" + k] = pick(p[k], LV.map(([v]) => v)); });
    ["disk", "ram", "cpu"].forEach((k) => { init["h-" + k] = globalHealth(k) ?? ""; });
    return init;
  })());
  const v = (k: string) => f.d[k] ?? "";
  const pnote = pa ? null : <p className="hint" style={{ marginTop: 0 }}>Platform-wide setting, managed by your platform administrator.</p>;
  const platBadge = pa ? null : <><div className="spacer" /><span className="badge">platform</span></>;
  const chk = (id: string, title: ReactNode, sub: ReactNode) => (
    <label className={"check " + (f.d[id] ? "on" : "")}>
      <input type="checkbox" checked={!!f.d[id]} disabled={dis} onChange={(e) => f.set(id, e.target.checked)} />
      <span><b>{title}</b><small>{sub}</small></span></label>
  );
  const inp = (id: string, label: ReactNode, type = "text", hint: ReactNode = "", d = dis) => (
    <label className="f"><span>{label}</span>
      <input type={type} value={v(id)} disabled={d} onChange={(e) => f.set(id, e.target.value)} />
      {hint ? <div className="hint">{hint}</div> : null}</label>
  );
  const pinp = (id: string, label: ReactNode, type = "text", hint: ReactNode = "") => inp(id, label, type, hint, pdis);
  const custom = f.d["p-profile"] === "custom";
  const install = `curl -fsSL ${DB.meta.public_url}/api/agent/install.sh | bash -s -- ${DB.meta.enroll_key}`;
  const pub = String(DB.meta.pubkey ?? "");
  return (
    <>
      <div className="pagehead"><div><h2>Settings</h2><p>Defaults that apply to every device and deployment in this console.</p></div></div>
      <div className="grid g2">
        <div className="card"><div className="hd"><h3>Deployment policy</h3></div><div className="bd">
          <label className="f"><span>Profile</span>
            <select value={v("p-profile")} disabled={dis} onChange={(e) => Settings.profile(f, e.target.value)}>
              {PROFILES.map(([pv, t]) => <option key={pv} value={pv}>{t}</option>)}
            </select>
            <div className="hint">Applies to every compose file validated or deployed in <b>{DB.org.org || "this organization"}</b>.</div></label>
          <table className="rules" style={{ marginBottom: 12 }}><tbody>
            {RULES.map(([k, t]) => (
              <tr key={k}><td style={{ padding: "4px 0" }}>{t}</td><td style={{ padding: "4px 0", textAlign: "right" }}>
                <select value={v("p-" + k)} disabled={dis || !custom} data-rule={k} onChange={(e) => f.set("p-" + k, e.target.value)}>
                  {LV.map(([lv, lt]) => <option key={lv} value={lv}>{lt}</option>)}
                </select></td></tr>
            ))}
          </tbody></table>
          {chk("p-reg", "Restrict registries to an allow-list", "Images from other registries are rejected at validation.")}
          <label className="f"><span>Allowed registries (one per line)</span>
            <textarea className="plain" rows={4} disabled={dis} value={v("p-regs")} onChange={(e) => f.set("p-regs", e.target.value)} /></label>
          {chk("p-ovr", "Let Admins relax validation for a single deploy", "Shows a “Relaxed validation (Admin override)” switch in deploy wizards. Every use is written to the audit log.")}
          {ad ? <Btn primary onClick={() => Settings.savePolicy(f)}>Save policy</Btn> : null}
        </div></div>

        <div className="card"><div className="hd"><h3>Connectivity</h3>{platBadge}</div><div className="bd">{pnote}
          <div className="grid g3">{pinp("c-in", "Agent check-in (s)", "number")}{pinp("c-off", "Offline after (s)", "number")}{pinp("c-ret", "Alert data kept (days)", "number")}</div>
          {pinp("c-ov", "Overlay description")}
          <label className="f"><span>Tailscale auth key (for “join the overlay” and public URLs)</span>
            <input type="password" value={v("c-ts")} placeholder={c.has_tailscale_authkey ? "•••••••• saved — type to replace" : "tskey-auth-…"} disabled={pdis}
              autoComplete="new-password" onChange={(e) => f.set("c-ts", e.target.value)} />
            <div className="hint">Use a reusable, pre-approved key. Stored encrypted; never shown again.</div></label>
          {pinp("c-tags", "Tailscale tags for guests", "text", "Must be allowed in your tailnet ACL tagOwners. Leave empty for none.")}
          {ad && pa ? (
            <div className="btnrow">
              <Btn primary onClick={() => Settings.save("connectivity", {
                checkin_s: +v("c-in"), offline_after_s: +v("c-off"), retention_days: +v("c-ret"), overlay: v("c-ov"),
                tailscale_tags: v("c-tags"), tailscale_authkey: v("c-ts"),
              })}>Save</Btn>
              <button className="btn" onClick={() => Modal.confirm("Rotate the console SSH key?",
                "A new key is added to every device and guest, the console switches to it, then the old key is removed. Hosts that are unreachable keep the old key as a fallback.",
                "Rotate", () => fire(job("POST", "/api/settings/rotate-ssh-keys")))}>Rotate SSH keys</button>
            </div>) : null}
          <div className="section-title">Console SSH public key</div>
          <pre className="log copy" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }} onClick={() => copy(pub)} title="Click to copy">{pub}</pre>
          <div className="btnrow" style={{ marginTop: 6 }}><button className="btn sm" onClick={() => copy(pub)}>Copy key</button></div>
          {ad ? <>
            <div className="section-title">Enroll a new device</div>
            <pre className="log copy" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }} onClick={() => copy(install)} title="Click to copy">{install}</pre>
            <div className="btnrow" style={{ marginTop: 8 }}>
              <button className="btn sm" onClick={() => copy(install)}>Copy command</button>
              {pa ? <button className="btn sm ghost" onClick={() => Modal.confirm("Generate a new enrollment key?",
                "Devices that are already enrolled keep working. Old install commands stop working.", "Generate",
                () => fire(act("POST", "/api/settings/enrollment-key", undefined, "New enrollment key")))}>New enrollment key</button> : null}
            </div>
          </> : null}
        </div></div>

        <div className="card"><div className="hd"><h3>Device health</h3>{platBadge}</div><div className="bd">{pnote}
          <p style={{ marginTop: 0, color: "var(--ink-2)" }}>A device shows <b>degraded</b> when it reaches any of these. Devices can override them in their own Settings tab; 0 turns a check off. Alert rules are separate and only control notifications.</p>
          <div className="grid g3">{["disk", "ram", "cpu"].map((k) => <Fragment key={k}>{pinp("h-" + k, (HEALTH_LABELS as any)[k], "number")}</Fragment>)}</div>
          {ad && pa ? <Btn primary onClick={() => Settings.save("connectivity", { health_disk: +v("h-disk"), health_ram: +v("h-ram"), health_cpu: +v("h-cpu") })}>Save thresholds</Btn> : null}
        </div></div>

        <div className="card"><div className="hd"><h3>Maintenance window</h3>{platBadge}</div><div className="bd">{pnote}
          <label className="f"><span>Changes are allowed between</span>
            <select value={v("w-val")} disabled={pdis} onChange={(e) => f.set("w-val", e.target.value)}>
              {WINDOWS.map((o) => <option key={o}>{o}</option>)}
            </select></label>
          {pinp("w-tz", "Time zone", "text", "IANA name, e.g. Asia/Kolkata. Used by the window and all schedules.")}
          <p style={{ color: "var(--ink-2)", margin: "0 0 12px" }}>Jobs marked “next maintenance window”, scheduled jobs and rollouts wait for the window. A site can override it.</p>
          {ad && pa ? <Btn primary onClick={() => Settings.save("window", { value: v("w-val"), tz: v("w-tz") })}>Save</Btn> : null}
        </div></div>

        <div className="card"><div className="hd"><h3>Branding</h3></div><div className="bd">
          {inp("b-name", "Console name shown to customers")}
          {inp("b-dom", "Custom domain", "text", "Point it at this server and put a TLS proxy in front (see README).")}
          {pinp("g-user", "Default user created in new VMs (platform)")}
          {ad ? <Btn primary onClick={async () => {
            await Settings.save("branding", { name: v("b-name"), domain: v("b-dom") });
            if (pa) await Settings.save("guest", { vm_user: v("g-user") }, true);
          }}>Save changes</Btn> : null}
        </div></div>

        {pa ? (
          <div className="card"><div className="hd"><h3>Notifications</h3></div><div className="bd">
            <div className="grid g2">{inp("n-host", "SMTP host")}{inp("n-port", "SMTP port", "number")}</div>
            <div className="grid g2">{inp("n-user", "SMTP user")}
              <label className="f"><span>SMTP password</span>
                <input type="password" value={v("n-pass")} placeholder={nf.has_smtp_pass ? "•••••••• saved" : ""} disabled={dis}
                  autoComplete="new-password" onChange={(e) => f.set("n-pass", e.target.value)} /></label></div>
            <div className="grid g2">{inp("n-from", "From address")}{inp("n-to", "Send alerts to")}</div>
            {inp("n-slack", "Slack incoming webhook URL")}{inp("n-pd", "PagerDuty routing key")}{inp("n-hook", "Generic webhook URL (JSON POST)")}
            {ad ? (
              <div className="btnrow">
                <Btn primary onClick={() => Settings.save("notifications", {
                  smtp_host: v("n-host"), smtp_port: +v("n-port"), smtp_user: v("n-user"), smtp_pass: v("n-pass"), smtp_from: v("n-from"),
                  email_to: v("n-to"), slack_webhook: v("n-slack"), pagerduty_key: v("n-pd"), webhook_url: v("n-hook"),
                })}>Save</Btn>
                <Btn onClick={() => Settings.test()}>Send a test</Btn>
              </div>) : null}
          </div></div>
        ) : null}
      </div>
    </>
  );
}
PAGES.settings = { crumb: () => "Settings", C: SettingsPage };
